import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
} from './legacy-ball/table-management/designTokens';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ErrorReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PageInfo {
  id: string;
  slug: string;
  displayName: string;
  alias: string | null;
}

interface ParsedUserAgent {
  browserName: string;
  browserVersion: string;
  deviceOs: string;
}

type Severity = 'Low' | 'Medium' | 'High' | 'Critical';

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatTimestamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  const hoursStr = String(hours12).padStart(2, '0');

  return `${year}-${month}-${day} ${hoursStr}:${minutes} ${period}`;
}

function parseUserAgent(ua: string): ParsedUserAgent {
  const result: ParsedUserAgent = {
    browserName: 'Unknown',
    browserVersion: 'Unknown',
    deviceOs: 'Unknown',
  };

  if (!ua) return result;

  // Detect OS
  if (/Windows NT 10/i.test(ua)) {
    result.deviceOs = 'Windows 10';
  } else if (/Windows NT 11/i.test(ua)) {
    result.deviceOs = 'Windows 11';
  } else if (/Windows NT/i.test(ua)) {
    result.deviceOs = 'Windows';
  } else if (/Mac OS X ([\d_]+)/i.test(ua)) {
    const match = ua.match(/Mac OS X ([\d_]+)/i);
    if (match) result.deviceOs = `macOS ${match[1].replace(/_/g, '.')}`;
  } else if (/Android ([\d.]+)/i.test(ua)) {
    const match = ua.match(/Android ([\d.]+)/i);
    result.deviceOs = match ? `Android ${match[1]}` : 'Android';
  } else if (/iPhone OS ([\d_]+)/i.test(ua)) {
    const match = ua.match(/iPhone OS ([\d_]+)/i);
    result.deviceOs = match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/iPad/i.test(ua)) {
    result.deviceOs = 'iPadOS';
  } else if (/Linux/i.test(ua)) {
    result.deviceOs = 'Linux';
  }

  // Detect browser (order matters)
  if (/Edg\/([\d.]+)/i.test(ua)) {
    const match = ua.match(/Edg\/([\d.]+)/i);
    result.browserName = 'Edge';
    result.browserVersion = match ? match[1] : 'Unknown';
  } else if (/OPR\/([\d.]+)/i.test(ua)) {
    const match = ua.match(/OPR\/([\d.]+)/i);
    result.browserName = 'Opera';
    result.browserVersion = match ? match[1] : 'Unknown';
  } else if (/Chrome\/([\d.]+)/i.test(ua)) {
    const match = ua.match(/Chrome\/([\d.]+)/i);
    result.browserName = 'Chrome';
    result.browserVersion = match ? match[1] : 'Unknown';
  } else if (/Firefox\/([\d.]+)/i.test(ua)) {
    const match = ua.match(/Firefox\/([\d.]+)/i);
    result.browserName = 'Firefox';
    result.browserVersion = match ? match[1] : 'Unknown';
  } else if (/Safari\/([\d.]+)/i.test(ua) && /Version\/([\d.]+)/i.test(ua)) {
    const match = ua.match(/Version\/([\d.]+)/i);
    result.browserName = 'Safari';
    result.browserVersion = match ? match[1] : 'Unknown';
  }

  return result;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ErrorReportModal({ isOpen, onClose }: ErrorReportModalProps) {
  const location = useLocation();
  const { user } = useAuth();

  // Environmental data (auto-captured)
  const [screenSlug, setScreenSlug] = useState('');
  const [screenAlias, setScreenAlias] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [browserInfo, setBrowserInfo] = useState<ParsedUserAgent>({
    browserName: 'Unknown',
    browserVersion: 'Unknown',
    deviceOs: 'Unknown',
  });

  // Editable fields
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [severity, setSeverity] = useState<Severity | ''>('');

  // Validation errors
  const [errors, setErrors] = useState<{ description?: string; severity?: string }>({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Auto-capture environmental data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Screen slug from full path (strip UUID segments)
    const slug = location.pathname
      .split('/')
      .filter(Boolean)
      .filter((seg) => !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg) && !/^[0-9a-f]{24,}$/i.test(seg))
      .join('/') || 'root';
    setScreenSlug(slug);

    // Current URL
    setCurrentUrl(window.location.href);

    // User info
    setUserName(user?.fullName || 'Unknown');
    setUserEmail(user?.email || 'Unknown');

    // Timestamp
    setTimestamp(formatTimestamp(new Date()));

    // Browser / device
    setBrowserInfo(parseUserAgent(navigator.userAgent));

    // Fetch screen alias
    apiClient<PageInfo>(`/page-registry/${slug}`)
      .then((data) => setScreenAlias(data.alias))
      .catch(() => setScreenAlias(null));

    // Reset form fields
    setDescription('');
    setStepsToReproduce('');
    setSeverity('');
    setErrors({});
    setSubmitError(null);
  }, [isOpen, location.pathname, user]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validate = useCallback((): boolean => {
    const newErrors: { description?: string; severity?: string } = {};
    const trimmedDesc = description.trim();

    if (trimmedDesc.length < 10) {
      newErrors.description = 'Description must be at least 10 characters.';
    } else if (trimmedDesc.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or fewer.';
    }

    if (!severity) {
      newErrors.severity = 'Please select a severity level.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [description, severity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setSubmitting(true);

    try {
      await apiClient('/error-reports', {
        method: 'POST',
        body: {
          screenSlug,
          screenAlias,
          currentUrl,
          browserName: browserInfo.browserName,
          browserVersion: browserInfo.browserVersion,
          deviceOs: browserInfo.deviceOs,
          description: description.trim(),
          stepsToReproduce: stepsToReproduce.trim() || undefined,
          severity,
        },
      });

      // Success — close modal and show confirmation
      onClose();
      alert('✅ Bug report submitted successfully! Thank you for helping us improve.');
    } catch (err: unknown) {
      const errorObj = err as { message?: string };
      setSubmitError(errorObj?.message || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleCancel} role="dialog" aria-modal="true" aria-labelledby="error-report-title">
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 id="error-report-title" style={styles.title}>Report a Bug 🐛</h2>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.closeBtn}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Read-only Environmental Data Section */}
          <div style={styles.envSection}>
            <h3 style={styles.sectionLabel}>Environmental Data (auto-captured)</h3>
            <div style={styles.envGrid}>
              <EnvField label="Screen" value={screenAlias ? `${screenSlug} • ${screenAlias}` : screenSlug} />
              <EnvField label="URL" value={currentUrl} />
              <EnvField label="User" value={userName} />
              <EnvField label="Email" value={userEmail} />
              <EnvField label="Timestamp" value={timestamp} />
              <EnvField label="Browser" value={`${browserInfo.browserName} ${browserInfo.browserVersion}`} />
              <EnvField label="OS" value={browserInfo.deviceOs} />
            </div>
          </div>

          {/* Separator */}
          <hr style={styles.separator} />

          {/* User Input Section */}
          <div style={styles.inputSection}>
            {/* Description */}
            <div style={styles.fieldGroup}>
              <label htmlFor="err-description" style={styles.label}>
                Description <span style={styles.required}>*</span>
              </label>
              <textarea
                id="err-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the bug you encountered (min 10 characters)..."
                style={styles.textarea}
                maxLength={2000}
                rows={4}
              />
              <div style={styles.fieldMeta}>
                <span style={styles.charCount}>{description.length} / 2000</span>
                {errors.description && <span style={styles.errorText}>{errors.description}</span>}
              </div>
            </div>

            {/* Steps to Reproduce */}
            <div style={styles.fieldGroup}>
              <label htmlFor="err-steps" style={styles.label}>
                Steps to Reproduce <span style={styles.optional}>(optional)</span>
              </label>
              <textarea
                id="err-steps"
                value={stepsToReproduce}
                onChange={(e) => setStepsToReproduce(e.target.value)}
                placeholder="1. Go to page X&#10;2. Click on Y&#10;3. Observe Z..."
                style={styles.textarea}
                maxLength={2000}
                rows={3}
              />
              <div style={styles.fieldMeta}>
                <span style={styles.charCount}>{stepsToReproduce.length} / 2000</span>
              </div>
            </div>

            {/* Severity */}
            <div style={styles.fieldGroup}>
              <label htmlFor="err-severity" style={styles.label}>
                Severity <span style={styles.required}>*</span>
              </label>
              <select
                id="err-severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity | '')}
                style={styles.select}
              >
                <option value="">— Select severity —</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
              {errors.severity && <span style={styles.errorText}>{errors.severity}</span>}
            </div>
          </div>

          {/* Submit Error */}
          {submitError && (
            <div style={styles.submitError}>{submitError}</div>
          )}

          {/* Actions */}
          <div style={styles.actions}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelBtn}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Environmental Field Sub-component ────────────────────────────────────────

function EnvField({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.envField}>
      <span style={styles.envLabel}>{label}:</span>
      <span style={styles.envValue}>{value}</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 12px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.2rem',
    color: TEXT_MUTED,
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  form: {
    padding: '0 24px 24px',
  },
  envSection: {
    backgroundColor: BG_SURFACE,
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '16px',
  },
  sectionLabel: {
    margin: '0 0 8px 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  envGrid: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  envField: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    fontSize: '0.8rem',
    lineHeight: 1.4,
  },
  envLabel: {
    color: TEXT_MUTED,
    fontWeight: 600,
    minWidth: '70px',
    flexShrink: 0,
  },
  envValue: {
    color: TEXT_DARK,
    wordBreak: 'break-all' as const,
  },
  separator: {
    border: 'none',
    borderTop: `1px solid ${BORDER_LIGHT}`,
    margin: '16px 0',
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  required: {
    color: '#d32f2f',
  },
  optional: {
    color: TEXT_MUTED,
    fontWeight: 400,
    fontSize: '0.8rem',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.85rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.85rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
  },
  fieldMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  charCount: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  errorText: {
    fontSize: '0.75rem',
    color: '#d32f2f',
    fontWeight: 500,
  },
  submitError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '0.8rem',
    marginTop: '12px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  cancelBtn: {
    padding: '10px 20px',
    fontSize: '0.85rem',
    fontWeight: 500,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: '#ffffff',
    color: TEXT_DARK,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    borderRadius: '6px',
    border: 'none',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    cursor: 'pointer',
  },
};
